import { useEffect, useMemo, useRef } from 'react'
import { Form } from 'antd'
import type { FormInstance } from 'antd'

/**
 * pbo-core `logistics.ShipperSelect`(화주유형/화주명 한 칸) 를 쓰는 필터의 공통 배선.
 *
 * 화면마다 옵션을 따로 만들던 것을 여기로 모은다(LOGISPRD-12609). 두 가지를 함께 처리한다.
 *
 * 1. 화주유형을 고르면 화주명 목록을 그 유형으로 좁히고, 사용자가 유형을 바꿨을 때 남아 있던 다른
 *    유형의 선택은 비운다. 안 그러면 M1P 화주를 고른 채 유형만 MFS 로 바꿔 존재할 수 없는 조합으로
 *    조회가 나가고, 사용자는 0건을 데이터 없음으로 읽는다.
 * 2. 목록 항목에 화주코드를 함께 보인다. 화주명은 겹칠 수 있다 — dev 기준 `29CM_NIKESAMPLE` 은
 *    MWP-60 · MWP-314 · MWP-316 · MWP-317 네 화주가 같은 이름을 쓴다.
 *
 * 값(value)이 화주코드인 화면(RGR04·GR02)과 화주명인 화면이 갈려 `valueKey` 로 받는다.
 */

/** 옵션을 만드는 데 필요한 최소 필드. `ShipperItem` 이 그대로 들어맞는다. */
export interface ShipperOptionSource {
  shipperCode: string
  shipperName: string
  logisticsServiceType: string
}

export interface ShipperNameOption {
  value: string
  label: string
}

/** 화주명 옵션의 value 로 쓸 필드. BE 계약이 화면마다 달라 호출부가 고른다. */
export type ShipperNameValueKey = 'shipperCode' | 'shipperName'

/** 한 항목 라벨에 늘어놓을 화주코드 최대 개수. 넘치면 `외 n` 으로 줄인다. */
export const SHIPPER_CODE_LABEL_LIMIT = 3

/**
 * 화주 칸에 얹을 필수 설정 — 별표는 남기고 칸 단위 필수 검증은 끈다.
 *
 * 화주는 유형과 명 중 하나만 채우면 되는데 `FilterFieldSpec.required` 는 칸 하나짜리 스위치라
 * 그 조건을 못 적는다. pbo-core 0.7.10 `FilterArea` 는 이 값 하나로 별표(`requiredMark`)와 필수
 * rule 을 함께 만들고, rule 은 화주유형 칸에만 건다(`renderField`·`ShipperSelectField` 실측).
 * 조회 버튼은 `Form onFinish` 라 rule 이 걸리면 `onSearch` 자체가 안 불린다 — 화주명만 고른
 * 조회가 "필수 입력 값을 입력해 주세요." 로 막혔다(dev-2 E2E 실측 2026-09-09).
 *
 * `allOption` 이 켜져 있으면 `FilterArea` 는 별표만 남기고 rule 배열을 비운다. 이 값은
 * `logistics.ShipperSelect` 로 전달되지 않으므로(renderField 가 넘기는 prop 목록에 없다)
 * '전체' 옵션이 생기는 등의 다른 변화는 없다. 실제 차단은 각 화면의 `hasRequiredFilter` 가 한다.
 *
 * pbo-core 를 올릴 때 이 두 전제가 그대로인지 본다. 깨지면 두 화면의 필터 테스트
 * (`화주유형을 비우고 화주명만 골라도 조회가 나간다`)가 먼저 걸린다.
 */
export const SHIPPER_REQUIRED_MARK_ONLY = { required: true, allOption: true } as const

/** 폼 값이 배열로도 스칼라로도 오는 칸을 목록으로 맞춘다(`nameMode` 미전달 이슈 — 각 화면 constants 주석). */
const toList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean)
  if (typeof value === 'string') return value.trim() ? [value.trim()] : []
  return []
}

/**
 * 두 선택이 같은 값들을 담고 있는지. 유형·화주 선택이 실제로 바뀌었는지 가리는 데 쓴다.
 *
 * 셀렉트 값은 순서가 아니라 집합이라 순서는 보지 않는다 — 같은 유형을 순서만 바꿔 다시 고른 것은
 * 유형이 바뀐 게 아니다(리뷰 반영).
 */
const sameSelection = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false
  const values = new Set(a)
  return b.every((value) => values.has(value))
}

const toLabel = (name: string, codes: string[]): string => {
  if (codes.length === 0) return name
  const shown = codes.slice(0, SHIPPER_CODE_LABEL_LIMIT)
  const rest = codes.length - shown.length
  return `${name} (${shown.join(', ')}${rest > 0 ? ` 외 ${rest}` : ''})`
}

export interface BuildShipperNameOptionsParams {
  valueKey: ShipperNameValueKey
  /** 선택된 화주유형. 비어 있으면 좁히지 않는다(유형 미선택 = 전체). */
  serviceTypes?: string[]
}

/**
 * 화주 목록 → 화주명 셀렉트 옵션. 선택된 화주유형으로 좁히고, 라벨에 화주코드를 붙인다.
 *
 * 같은 value 로 묶이는 화주가 여럿이면(화주명이 value 인 화면) 코드를 모두 모아 보인다 —
 * 그 이름을 고르면 실제로 그 코드들이 모두 조회 대상이기 때문이다.
 * 코드가 이름과 같으면(MUSINSA 등) 덧붙이지 않는다.
 */
export const buildShipperNameOptions = (
  shippers: readonly ShipperOptionSource[],
  { valueKey, serviceTypes = [] }: BuildShipperNameOptionsParams,
): ShipperNameOption[] => {
  const allowedTypes = new Set(serviceTypes.filter(Boolean))
  const grouped = new Map<string, { name: string; codes: string[] }>()

  shippers.forEach((shipper) => {
    if (allowedTypes.size > 0 && !allowedTypes.has(shipper.logisticsServiceType)) return

    const code = shipper.shipperCode?.trim() ?? ''
    const name = shipper.shipperName?.trim() ?? ''
    const value = (valueKey === 'shipperCode' ? code : name) || code || name
    if (!value) return

    const entry = grouped.get(value) ?? { name: name || value, codes: [] }
    if (code && code !== entry.name && !entry.codes.includes(code)) entry.codes.push(code)
    grouped.set(value, entry)
  })

  return Array.from(grouped, ([value, { name, codes }]) => ({ value, label: toLabel(name, codes) }))
}

export interface UseShipperNameFilterOptionsParams {
  /** `FilterArea` 에 넘긴 것과 같은 antd 폼. */
  form: FormInstance
  /** 화주유형 칸의 폼 필드명(`FilterFieldSpec.typeName`). */
  typeFieldName: string
  /** 화주명 칸의 폼 필드명(`FilterFieldSpec.nameName`). */
  nameFieldName: string
  shippers: readonly ShipperOptionSource[]
  valueKey: ShipperNameValueKey
  /**
   * 초기화 세대(화면의 `resetKey`). 값이 바뀐 직후 한 번은 비우지 않고 기준만 다시 잡는다.
   *
   * 초기화는 기본 화주유형(M1P)과 세션 화주를 함께 심는데, 로그인 화주가 MWP·MFS 면 화주 값이
   * 그대로라 "유형만 바뀌었다" 로 읽힌다. 그대로 두면 초기화가 필수값인 자기 화주를 지운다
   * (리뷰 반영, RGR04). 넘기지 않으면 이 보호가 없다.
   */
  resetKey?: number | string
}

/**
 * 화주명 옵션을 만들어 돌려주고, 사용자가 화주유형을 바꿔 못 고르게 된 화주명 선택을 폼에서 걷어낸다.
 *
 * 비우는 기준은 "지금 옵션에 없다" 가 아니라 "화주유형만 바뀌었다" 다. 화면은 화주유형이 비면
 * 기본값을 채우면서(RGR04 `searchQuery.ts` — M1P) 로그인 화주코드는 세션 값 그대로 둔다. 옵션에
 * 없다는 이유만으로 비우면 MWP·MFS 로그인 화주가 진입하자마자, 유형을 손댄 적도 없이 자기 화주를
 * 잃는다(리뷰 반영). 그래서 직전 관측과 견줘 화주 선택은 그대로인 채 유형만 바뀐 경우에만 비운다.
 * 초기값·URL 복원값은 유형과 화주를 한 번에 심으므로 이 검사에 걸리지 않고, 화주 값까지 같아
 * 걸러지지 않는 초기화는 `resetKey` 로 따로 건너뛴다.
 *
 * 관측값은 useWatch 가 아니라 폼에서 직접 읽는다. useWatch 는 심은 값보다 한 렌더 늦게 따라와,
 * 목록이 이미 캐시에 있는 채로 진입하면 유형만 뒤늦게 바뀐 것처럼 보여 화주가 지워진다.
 * useWatch 는 효과를 다시 돌릴 신호와 옵션 계산에만 쓰고, 그 둘이 어긋난 렌더에서는 판정하지 않는다.
 *
 * 원본 목록(`shippers`)이 비어 있는 동안은 판정 자체를 미룬다 — 좁힐 근거가 없다.
 */
export const useShipperNameFilterOptions = ({
  form,
  typeFieldName,
  nameFieldName,
  shippers,
  valueKey,
  resetKey,
}: UseShipperNameFilterOptionsParams): ShipperNameOption[] => {
  const watchedTypes = Form.useWatch(typeFieldName, form)
  // 화주명도 지켜본다. 이 값이 없으면 화주명만 바뀐 렌더에서 효과가 돌지 않아 기준이 옛 값에 머물고,
  // 그 다음 유형 변경이 "화주도 같이 바뀌었다" 로 잘못 읽혀 비우기가 통째로 건너뛰어진다(리뷰 반영).
  const watchedNames = Form.useWatch(nameFieldName, form)
  const serviceTypes = useMemo(() => toList(watchedTypes), [watchedTypes])

  const nameOptions = useMemo(
    () => buildShipperNameOptions(shippers, { valueKey, serviceTypes }),
    [shippers, valueKey, serviceTypes],
  )

  /** 직전에 관측한 유형·화주 선택. 유형만 바뀐 렌더를 골라내는 기준이다. */
  const observedRef = useRef<{ types: string[]; names: string[] } | null>(null)
  /** 기준을 다시 잡은 초기화 세대. */
  const resetKeyRef = useRef(resetKey)

  useEffect(() => {
    // 세대 비교·갱신은 아래 early return 보다 먼저 한다. 초기화 시점에 화주 목록이 아직 없으면
    // (또는 폼이 아직 안 붙었으면) 효과가 그대로 빠져나가는데, 그때 세대를 남겨 두면 그 초기화가
    // 미뤄졌다가 **사용자의 다음 유형 변경**에서 소비된다 — 비우기가 통째로 건너뛰어져 못 고르게 된
    // 화주명이 남는다(리뷰 반영). 세대는 판정을 건너뛰는 근거일 뿐이라 여기서 소비해도 잃는 게 없다.
    const generationChanged = resetKeyRef.current !== resetKey
    resetKeyRef.current = resetKey

    if (shippers.length === 0) return
    if (typeof form?.getFieldValue !== 'function' || typeof form?.setFieldValue !== 'function') return

    const currentTypes = toList(form.getFieldValue(typeFieldName))
    const current = form.getFieldValue(nameFieldName)
    const selected = toList(current)
    const observed = observedRef.current
    observedRef.current = { types: currentTypes, names: selected }

    // 첫 관측은 기준만 잡는다. 화주 선택이 함께 바뀐 렌더도 사용자의 유형 변경이 아니다.
    if (!observed) return
    // 초기화 직후 한 번은 판정하지 않는다 — 기본 유형과 세션 화주를 함께 심는데 화주 값이 같아
    // 유형만 바뀐 것처럼 보인다.
    if (generationChanged) return
    // 옵션은 useWatch 값으로 만든다. 폼이 앞서 있으면 이 렌더의 옵션은 옛 유형 기준이라 판정을 미룬다.
    if (!sameSelection(currentTypes, serviceTypes)) return
    if (sameSelection(observed.types, currentTypes) || !sameSelection(observed.names, selected)) return
    if (selected.length === 0) return

    const allowed = new Set(nameOptions.map((option) => option.value))
    const kept = selected.filter((value) => allowed.has(value))
    if (kept.length === selected.length) return

    // 값 모양(배열/스칼라)은 원래대로 돌려준다 — 화면마다 요청 변환이 다르다.
    const next = Array.isArray(current) ? kept : (kept[0] ?? undefined)
    // 방금 비운 값을 기준으로 남긴다 — 다음 관측이 이 변화를 사용자의 화주 선택으로 읽지 않게 한다.
    observedRef.current = { types: currentTypes, names: toList(next) }
    form.setFieldValue(nameFieldName, next)
  }, [nameOptions, nameFieldName, typeFieldName, shippers, form, serviceTypes, watchedNames, resetKey])

  return nameOptions
}
